import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import Header from "./Header";
import Footer from "./Footer";
import Icon from "./Icon";
import ProductCard, { ProductCardSkeleton } from "./ProductCard";
import CartPanel from "./CartPanel";
import { errorMessage, fetchCart, fetchProducts, isAuthError, searchProducts, setCartQty } from "../api";
import { clearSession, useSession } from "../session";
import SortMenu from "./SortMenu";
import { SORTS, cartLines, cartTotals, categoryStyle, money, sortProducts } from "../utils";
import "./ShopPage.css";

const SLOW_MS = 3000;

const ShopPage = () => {
  const session = useSession();
  const history = useHistory();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [slow, setSlow] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ query: "", items: [] });

  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("featured");

  const [cart, setCart] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const gridRef = useRef(null);
  const drawerCloseRef = useRef(null);

  const handleAuthFailure = useCallback(() => {
    clearSession();
    setCart([]);
    enqueueSnackbar("Your session has ended. Please log in again.", { variant: "warning" });
  }, [enqueueSnackbar]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    const timer = setTimeout(() => setSlow(true), SLOW_MS);
    try {
      setProducts(await fetchProducts());
    } catch (e) {
      setLoadFailed(true);
      enqueueSnackbar(errorMessage(e), { variant: "error" });
    } finally {
      clearTimeout(timer);
      setSlow(false);
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (!session.loggedIn) {
      setCart([]);
      return;
    }
    fetchCart()
      .then(setCart)
      .catch((e) => (isAuthError(e) ? handleAuthFailure() : enqueueSnackbar(errorMessage(e), { variant: "error" })));
  }, [session.loggedIn, handleAuthFailure, enqueueSnackbar]);

  // Debounced search against the API (matches product names and categories).
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchText.trim()), 450);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!query) return;
    let active = true;
    searchProducts(query)
      .then((items) => active && setResults({ query, items }))
      .catch((e) => {
        if (!active) return;
        setResults({ query, items: [] });
        enqueueSnackbar(errorMessage(e), { variant: "error" });
      });
    return () => {
      active = false;
    };
  }, [query, enqueueSnackbar]);

  // Close the drawer with Escape and keep the page behind it still.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (event) => event.key === "Escape" && setDrawerOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (drawerCloseRef.current) drawerCloseRef.current.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const searching = Boolean(searchText.trim());
  const searchPending = searching && (searchText.trim() !== query || results.query !== query);

  const counts = useMemo(
    () => products.reduce((acc, p) => ({ ...acc, [p.category]: (acc[p.category] || 0) + 1 }), {}),
    [products]
  );
  const categories = useMemo(
    () => Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b)),
    [counts]
  );

  const visible = useMemo(() => {
    const source = searching ? results.items : products;
    const filtered = category === "All" ? source : source.filter((p) => p.category === category);
    return sortProducts(filtered, sort);
  }, [searching, results, products, category, sort]);

  const lines = useMemo(() => cartLines(cart, products), [cart, products]);
  const totals = cartTotals(lines);
  const qtyOf = (id) => (cart.find((item) => item.productId === id) || {}).qty || 0;

  const changeQty = async (product, qty) => {
    setBusyId(product._id);
    try {
      setCart(await setCartQty(product._id, Math.max(0, qty)));
    } catch (e) {
      if (isAuthError(e)) handleAuthFailure();
      else enqueueSnackbar(errorMessage(e), { variant: "error" });
    } finally {
      setBusyId(null);
    }
  };

  const addToCart = (product) => {
    if (!session.loggedIn) {
      enqueueSnackbar("Log in to add items to your cart", {
        variant: "info",
        action: (key) => (
          <button
            type="button"
            className="snack-action"
            onClick={() => {
              closeSnackbar(key);
              history.push("/login");
            }}
          >
            Log in
          </button>
        ),
      });
      return;
    }
    changeQty(product, 1);
  };

  const pickCategory = (name) => {
    setCategory(name);
    if (gridRef.current) gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const clearFilters = () => {
    setSearchText("");
    setQuery("");
    setCategory("All");
    setSort("featured");
  };

  const cartAside = session.loggedIn ? (
    <CartPanel lines={lines} balance={session.balance} busyId={busyId} onChangeQty={changeQty} />
  ) : (
    <div className="cart-login">
      <span className="cart-empty-icon" aria-hidden="true">
        <Icon name="bag" size={26} />
      </span>
      <h2 id="cart-title" className="cart-title">
        Start your cart
      </h2>
      <p>Log in to add items and check out. New accounts get {money(5000)} in wallet credit.</p>
      <div className="cart-login-actions">
        <Link to="/login" className="btn btn-primary">
          Log in
        </Link>
        <Link to="/register" className="btn btn-ghost">
          Create account
        </Link>
      </div>
    </div>
  );

  let heading = category === "All" ? "All products" : category;
  if (searching) heading = `Results for “${searchText.trim()}”`;
  const showSkeleton = loading || searchPending;

  return (
    <>
      <Header>
        <form role="search" className="search" onSubmit={(e) => e.preventDefault()}>
          <Icon name="search" size={18} />
          <label htmlFor="product-search" className="sr-only">
            Search products
          </label>
          <input
            id="product-search"
            type="search"
            placeholder="Search products or categories"
            autoComplete="off"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </form>
      </Header>

      <main className="page container shop" id="main">
        {!searching && (
          <section className="hero" aria-labelledby="hero-title">
            <div className="hero-banner">
              <span className="hero-pill">Fastest delivery</span>
              <h1 id="hero-title" className="hero-title">
                India’s fastest delivery to your doorstep
              </h1>
              <p className="hero-text">Trending picks across fashion, electronics, home, sports and books.</p>
            </div>
            <nav className="hero-categories" aria-label="Shop by category">
              {categories.slice(0, 4).map((name) => {
                const style = categoryStyle(name);
                return (
                  <button key={name} type="button" className="category-tile" onClick={() => pickCategory(name)}>
                    <span className="category-tile-icon" style={{ background: style.tint, color: style.ink }}>
                      <Icon path={style.icon} size={22} strokeWidth={1.8} />
                    </span>
                    <span className="category-tile-text">
                      <span className="category-tile-name">{name}</span>
                      <span className="category-tile-count">
                        {counts[name]} {counts[name] === 1 ? "product" : "products"}
                      </span>
                    </span>
                  </button>
                );
              })}
              {loading &&
                [0, 1, 2, 3].map((i) => <span key={i} className="category-tile skeleton" aria-hidden="true" />)}
            </nav>
          </section>
        )}

        <div className="toolbar" ref={gridRef}>
          <div className="chips" role="group" aria-label="Category">
            {["All", ...categories].map((name) => (
              <button
                key={name}
                type="button"
                className={`chip ${category === name ? "is-active" : ""}`}
                aria-pressed={category === name}
                onClick={() => setCategory(name)}
              >
                {name}
                <span className="chip-count">{name === "All" ? products.length : counts[name]}</span>
              </button>
            ))}
          </div>
          <SortMenu options={SORTS} value={sort} onChange={setSort} />
        </div>

        <div className="shop-layout">
          <section className="shop-products" id="products" aria-labelledby="products-title" tabIndex={-1}>
            <div className="products-head">
              <h2 id="products-title" className="products-title">
                {heading}
              </h2>
              <span className="products-count" aria-live="polite">
                {showSkeleton ? "Loading…" : `${visible.length} ${visible.length === 1 ? "product" : "products"}`}
              </span>
            </div>
            {slow && <p className="notice">Waking up the server. On the free plan the first load can take up to a minute.</p>}

            {loadFailed && !products.length ? (
              <div className="state-box" role="alert">
                <h2>We couldn't load the products</h2>
                <p>The server may be waking up or temporarily unavailable.</p>
                <button type="button" className="btn btn-primary" onClick={loadProducts}>
                  Try again
                </button>
              </div>
            ) : showSkeleton ? (
              <div className="product-grid">
                {Array.from({ length: 6 }, (_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            ) : visible.length ? (
              <div className="product-grid">
                {visible.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    qty={qtyOf(product._id)}
                    busy={busyId === product._id}
                    onAdd={() => addToCart(product)}
                    onChangeQty={(qty) => changeQty(product, qty)}
                  />
                ))}
              </div>
            ) : (
              <div className="state-box">
                <h2>No products found</h2>
                <p>Try a different search or category.</p>
                <button type="button" className="btn btn-primary" onClick={clearFilters}>
                  Show all products
                </button>
              </div>
            )}
          </section>

          <aside className={`shop-cart card ${session.loggedIn ? "is-logged-in" : ""}`} aria-labelledby="cart-title">
            {cartAside}
          </aside>
        </div>
      </main>

      {session.loggedIn && lines.length > 0 && (
        <div className="cart-bar">
          <button type="button" className="cart-bar-btn" onClick={() => setDrawerOpen(true)}>
            <span className="cart-bar-icon" aria-hidden="true">
              <Icon name="bag" size={18} />
              <span className="cart-bar-badge">{totals.count}</span>
            </span>
            <span className="cart-bar-text">View cart</span>
            <span className="cart-bar-total">{money(totals.total)}</span>
          </button>
        </div>
      )}

      {drawerOpen && (
        <div className="drawer-root">
          <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <div className="drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-cart-title">
            <button
              ref={drawerCloseRef}
              type="button"
              className="icon-btn drawer-close"
              aria-label="Close cart"
              onClick={() => setDrawerOpen(false)}
            >
              <Icon name="close" size={18} />
            </button>
            <CartPanel
              lines={lines}
              balance={session.balance}
              busyId={busyId}
              onChangeQty={changeQty}
              headingId="drawer-cart-title"
            />
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ShopPage;
